<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';
import { useRouter } from 'vue-router';

import type { ITableColumnsOptions } from '@modules/tables/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import { TRANSLATION_GROUP_AFFILIATES_UNCONFIRMED } from '@modules/affiliates-unconfirmed/constants';
import { useUnconfirmedAffiliatesSendEmailMutation } from '@modules/affiliates-unconfirmed/mutations';
import {
  useAffiliatesUnconfirmedGetTokenQuery,
  useAffiliatesUnconfirmedListQuery,
} from '@modules/affiliates-unconfirmed/queries';
import { useCopyToClipboard } from '@modules/core/composables/copy';
import VFormTextarea from '@modules/forms/components/inputs/VFormTextarea/VFormTextarea.vue';
import { REGISTRATION_CONFIRMATION_ROUTE_NAME } from '@modules/registration/constants';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';

const {
  tableState,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(useAffiliatesUnconfirmedListQuery);

const columnOptions: ITableColumnsOptions = {
  email: {
    defaultMobilePreview: true,
  },
};

const router = useRouter();
const { copyToClipboard } = useCopyToClipboard();
const { mutateAsync: sendEmail } = useUnconfirmedAffiliatesSendEmailMutation();

const selectedAffiliateId = ref<number>();
const modalVisible = ref(false);

const { data: token } = useAffiliatesUnconfirmedGetTokenQuery(selectedAffiliateId);

const registrationLink = computed(() => {
  const link = router.resolve({
    name: REGISTRATION_CONFIRMATION_ROUTE_NAME,
    params: {
      token: token.value,
    },
  });

  return `${window.location.origin}${link.href}`;
});

function onViewToken(id: number): void {
  selectedAffiliateId.value = id;
  modalVisible.value = true;
}

function onModalHide(): void {
  selectedAffiliateId.value = undefined;
  modalVisible.value = false;
}

function onTokenCopy(): void {
  void copyToClipboard(registrationLink.value);
  modalVisible.value = false;
}
</script>

<template>
  <VCard v-data-qa-auto-id="'AffiliatesUnconfirmedListPage'" type="table">
    <VTable
      :data="data"
      :state="tableState"
      :loading="isLoading"
      :is-fetching="isFetching"
      :title-t-path="$getTPath(TRANSLATION_GROUP_AFFILIATES_UNCONFIRMED, 'HEADER')"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VIconButton
          icon="pi-send"
          @click="sendEmail(listItem.id)"
        />
        <VIconButton
          icon="pi-eye"
          @click="onViewToken(listItem.id)"
        />
      </template>
    </VTable>
    <VDialog
      v-model:visible="modalVisible"
      :title="$getTranslation(TRANSLATION_GROUP_AFFILIATES_UNCONFIRMED, 'MODAL_HEADER')"
      @hide="onModalHide"
    >
      <VFormTextarea
        readonly
        :value="registrationLink"
        :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_UNCONFIRMED, 'MODAL_URL_FOR_CONFIRMING_EMAIL')"
      />
      <template #footer>
        <VButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_UNCONFIRMED, 'MODAL_CANCEL')"
          outlined
          @click="onModalHide"
        />
        <VButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_UNCONFIRMED, 'MODAL_COPY')"
          @click="onTokenCopy"
        />
      </template>
    </VDialog>
  </VCard>
</template>
